/**
 * Planet-relative scale for things that sit on a planet surface: buildings,
 * ruins, rocks, flora, hazards, planted flags.
 *
 * Players and projectiles are the fixed unit of the game (a player is ~1.1
 * tall). Props used to be a fixed ×2 everywhere, which made a building as tall
 * as the person standing next to it, and on megaplanets left doors at knee
 * height. Props now grow with the world they stand on: tiny planets stay
 * toy-coherent, a radius-6 planet looks exactly as it did, and megaplanets
 * get shooter proportions (a door a player could walk through).
 *
 * Pure: both P2P peers derive identical sizes from the same planet radius.
 * The hit bonus keeps balance unchanged at or below the reference radius and
 * only widens hits where the prop is physically bigger than it used to be.
 */

/** Planet radius at which props are exactly their old ×2 size. */
export const PROP_SCALE_REFERENCE_RADIUS = 6;
export const PROP_SCALE_MIN = 1.5;   // tiny planets (r 1.5–3) — never smaller than this
export const PROP_SCALE_MAX = 6;     // megaplanets (r ≥ 18) — door ≈ 1.3, taller than a player

/** Authored half-widths of prop footprints in model-local units (before scaling). */
export const STRUCTURE_HALF_WIDTH = 0.5;   // kit buildings are ~1.0 local across
export const HAZARD_HALF_WIDTH = 0.25;

export function surfacePropScale(planetRadius: number): number {
  const s = 2 * planetRadius / PROP_SCALE_REFERENCE_RADIUS;
  return Math.max(PROP_SCALE_MIN, Math.min(PROP_SCALE_MAX, s));
}

/**
 * Extra blast reach against a prop because it is physically bigger than at the
 * reference radius. Zero at or below the reference (today's balance), growing
 * with the prop on larger worlds.
 */
export function propHitBonus(planetRadius: number, localHalfWidth: number): number {
  return Math.max(0, localHalfWidth * (surfacePropScale(planetRadius) - 2));
}
